import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Container as ContainerIcon, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, STATUS_META } from "../components/StatusBadge";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const TYRE_COUNTS = [2, 4, 6, 8, 10, 12, 16];
const SIZES = ["10ft", "20ft", "40ft", "45ft"];
const EMPTY = { code: "", size: "40ft", num_tyres: 8, notes: "" };

export default function TrailersPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === "management" || user?.role === "admin";
  const [trailers, setTrailers] = useState([]);
  const [maintIds, setMaintIds] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/trailers").then((r) => setTrailers(r.data)).catch(() => {});
    api.get("/fleet-status").then((r) => {
      const ids = new Set(r.data.maintenance_units.filter((u) => u.vehicle_type === "trailer").map((u) => u.vehicle_id));
      setMaintIds(ids);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 30000);

  const effectiveStatus = (t) => maintIds.has(t.id) ? "maintenance" : (t.manual_status || "siap_jalan");
  const setStatus = async (t, newStatus) => {
    try {
      await api.patch(`/trailers/${t.id}/status`, { manual_status: newStatus });
      toast.success(`Status diubah: ${STATUS_META[newStatus]?.label}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t, e) => {
    e?.stopPropagation();
    setEditing(t);
    setForm({ code: t.code, size: t.size, num_tyres: t.num_tyres, notes: t.notes || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/trailers/${editing.id}`, form);
        toast.success("Trailer diperbarui");
      } else {
        await api.post("/trailers", form);
        toast.success("Trailer berhasil ditambahkan");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (t, e) => {
    e?.stopPropagation();
    if (!confirm(`Hapus trailer ${t.code}?`)) return;
    try {
      await api.delete(`/trailers/${t.id}`);
      toast.success("Trailer dihapus");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Trailer / Gandengan</h1>
          <p className="text-slate-500 text-sm mt-1">Kelola trailer berdasarkan kode (S01, G01, G02, dst).</p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial h-12"
          data-testid="add-trailer-btn"
        >
          <Plus className="w-5 h-5 mr-1" /> Tambah Trailer
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {trailers.map((t) => (
          <div
            key={t.id}
            className="group border-2 border-slate-200 hover:border-slate-900 bg-white p-5 rounded-sm transition-colors"
            data-testid={`trailer-card-${t.code}`}
          >
            <div onClick={() => nav(`/vehicles/trailer/${t.id}`)} className="cursor-pointer" role="button" tabIndex={0}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Kode</div>
                  <div className="font-mono text-3xl font-black text-slate-900 mt-1">{t.code}</div>
                  <div className="mt-2">
                    <StatusBadge status={effectiveStatus(t)} testId={`trailer-status-${t.code}`} />
                  </div>
                </div>
                <div className="w-10 h-10 bg-slate-900 group-hover:bg-orange-500 flex items-center justify-center transition-colors">
                  <ContainerIcon className="w-5 h-5 text-orange-400 group-hover:text-slate-900" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <div className="text-xs">
                  <span className="font-mono font-bold text-slate-900">{t.size}</span>
                  <span className="text-slate-500"> · {t.num_tyres} ban</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-orange-500" />
              </div>
            </div>
            <div className="flex gap-1 mt-3 pt-3 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={(e) => openEdit(t, e)} data-testid={`edit-trailer-${t.code}`} className="flex-1 rounded-sm border-slate-300 hover:border-slate-900">
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              {canDelete && (
                <Button variant="outline" size="sm" onClick={(e) => del(t, e)} data-testid={`delete-trailer-${t.code}`} className="rounded-sm border-red-300 hover:border-red-600 hover:bg-red-50 text-red-600">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
            {!maintIds.has(t.id) && (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <Select value={t.manual_status || "siap_jalan"} onValueChange={(v) => setStatus(t, v)}>
                  <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`trailer-status-select-${t.code}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="siap_jalan">✅ Siap Jalan</SelectItem>
                    <SelectItem value="rusak">⚠ Rusak</SelectItem>
                    <SelectItem value="non_aktif">⏻ Non-Aktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ))}
        {trailers.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-12 border-2 border-dashed border-slate-300 rounded-sm">
            Belum ada trailer. Klik <b>Tambah Trailer</b> untuk mulai.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">
              {editing ? "Edit Trailer" : "Tambah Trailer"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Kode Trailer *</Label>
              <Input data-testid="trailer-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="h-12 rounded-sm font-mono uppercase" placeholder="G01, S02, dst" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Ukuran *</Label>
                <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="trailer-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Jumlah Ban *</Label>
                <Select value={String(form.num_tyres)} onValueChange={(v) => setForm({ ...form, num_tyres: Number(v) })}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="trailer-num-tyres"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYRE_COUNTS.map((n) => <SelectItem key={n} value={String(n)}>{n} ban</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="trailer-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                {busy ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
